/**
 * @file useXeroLock.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-03
 *
 * Xero's accounting lock, as the server holds it (services/xeroLockService):
 * `firstAllowedDate` is the earliest date a document can carry. Used to stop
 * date pickers at the lock and by the status bar's lock icon; the server
 * refuses a save regardless, and re-reads Xero before it does.
 *
 * One shared copy for the whole tab. It is filled by the first read here and
 * then kept current by the status poll (useSystemStatus reads
 * GET /api/admin/maintenance-lock every 20s, which carries the lock) and by a
 * refresh from the status bar — so when anyone refreshes the lock, every
 * open tab's icon follows within one poll.
 *
 *   const { firstAllowedDate, effectiveLockDate } = useXeroLock();
 */

import { useEffect, useState } from 'react';
import { API_URL } from '../config';

let current = null;
const listeners = new Set();
let pending = null;

/** Replace the shared copy and tell every mounted user of the hook. */
export function publishXeroLock(lock) {
  if (!lock) return;
  current = lock;
  listeners.forEach((fn) => fn(lock));
}

export default function useXeroLock() {
  const [lock, setLock] = useState(current);
  useEffect(() => {
    let live = true;
    const onChange = (v) => { if (live) setLock(v); };
    listeners.add(onChange);
    if (!current) {
      pending = pending || fetch(`${API_URL}/api/admin/xero-lock`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then(d => d?.xeroLock || null)
        .catch(() => null);
      pending.then(v => { if (v && !current) publishXeroLock(v); });
    }
    return () => { live = false; listeners.delete(onChange); };
  }, []);
  return {
    firstAllowedDate: lock?.firstAllowedDate || null,
    effectiveLockDate: lock?.effectiveLockDate || null,
    periodLockDate: lock?.periodLockDate || null,
    endOfYearLockDate: lock?.endOfYearLockDate || null,
    fetchedAt: lock?.fetchedAt || null,
    loaded: lock !== null
  };
}
